import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import './AccordanceDetail.scss';
import SaveButton from '../../Buttons/SaveButton/SaveButton';
import UndoButton from '../../Buttons/UndoButton/UndoButton';
import { AccordanceState } from '../../../../models/AccordanceState';
import Accordance from '../../../../models/Accordance';
import { getProject } from '../../../../redux/actions/currentProject';
import { useLocation } from 'react-router-dom';
import Project from '../../../../models/Project';
import { getFooterButtonsDecision } from './FooterButtons.helper';

type Props = {
  accordance: Accordance;
  isDraftValid: boolean;
  approveAcc: Function;
  rejectAcc: Function;
  deleteAcc: Function;
  setEditForm: Function;
  submitDraftToApproval: Function;
  closeModal: Function;
  changeAccordanceStatus: Function;
  hideApproveButtons?: boolean;
  isSubstantialModification?: boolean;
  confirmedModifiedSustantial?: boolean | null;
  proceedModification?: boolean | null;
  countTextArea?: number;
  rejectionJustification?: string;
  divChiefRole?: boolean,
  countryRepRole?: boolean,
  approvedByDivisionChief?: boolean,
  approvedByCountryRepresentative?: boolean,
  vps_managers?: boolean,
  vpc_managers?: boolean,
  approvedByVPSManager: boolean,
  approvedByVPCManager: boolean,
  canCreateOrEditAccordance?: boolean;
};

type RoleDecisionButtonsProps = {
  closeModal: Function;
  approveAcc: Function;
  rejectAcc: Function;
  rejectionJustification: string;
  canApproveDecision: boolean;
  canSubmitDecision: boolean;
  decisionText: string;
  approvalCompleted?: boolean;
  closeText: string;
};

const renderCloseButton = (closeModal: Function, closeText: string) => (
  <UndoButton handleClick={() => closeModal()} customText={closeText} />
);

const RoleDecisionButtons = ({
  closeModal,
  approveAcc,
  rejectAcc,
  rejectionJustification,
  canApproveDecision,
  canSubmitDecision,
  decisionText,
  approvalCompleted,
  closeText,
}: RoleDecisionButtonsProps) => {
  if (approvalCompleted) {
    return (
      <div className="approve-buttons">
        {renderCloseButton(closeModal, closeText)}
      </div>
    );
  }

  const handleDecision = canApproveDecision
    ? approveAcc
    : () => rejectAcc(rejectionJustification);

  return (
    <div className="approve-buttons">
      {renderCloseButton(closeModal, closeText)}
      <SaveButton
        handleClick={handleDecision}
        customText={decisionText}
        disabled={!canSubmitDecision}
      />
    </div>
  );
};

const FooterButtons = ({
  accordance,
  isDraftValid,
  approveAcc,
  rejectAcc,
  deleteAcc,
  setEditForm,
  submitDraftToApproval,
  closeModal,
  changeAccordanceStatus,
  hideApproveButtons,
  isSubstantialModification,
  confirmedModifiedSustantial,
  proceedModification,
  countTextArea = 0,
  rejectionJustification = '',
  divChiefRole,
  countryRepRole,
  approvedByDivisionChief,
  approvedByCountryRepresentative,
  vps_managers,
  vpc_managers,
  approvedByVPSManager,
  approvedByVPCManager,
  canCreateOrEditAccordance
}: Props) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const location = useLocation();

  const currentProject = useSelector<any, Project>(state => state.currentProject);

  React.useEffect(() => {
    dispatch(getProject(accordance.project.id));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const closeText = t('close');
  const approveText = t('approve_accordance');
  const rejectText = t('accordance_rejecting');
  const editText = t('edit_accordance');
  const deleteText = t('delete_accordance');
  const decision = getFooterButtonsDecision({
    accordance,
    isDraftValid,
    hideApproveButtons,
    isSubstantialModification,
    confirmedModifiedSustantial,
    proceedModification,
    countTextArea,
    rejectionJustification,
    divChiefRole,
    countryRepRole,
    approvedByDivisionChief,
    approvedByCountryRepresentative,
    vps_managers,
    vpc_managers,
    approvedByVPSManager,
    approvedByVPCManager,
    canCreateInterGroupAccordances: currentProject.canCreateInterGroupAccordances,
    projectAssignedToMe: currentProject.assignedToMe,
    canCreateOrEditAccordance
  });

  switch (decision.type) {
    case 'pending-role-decision':
      return (
        <RoleDecisionButtons
          closeModal={closeModal}
          approveAcc={approveAcc}
          rejectAcc={rejectAcc}
          rejectionJustification={rejectionJustification}
          canApproveDecision={decision.decisionAction === 'approve'}
          canSubmitDecision={decision.canSubmitDecision}
          decisionText={decision.decisionText === 'Confirmar' ? 'Confirmar' : t(decision.decisionText)}
          approvalCompleted={decision.approvalCompleted}
          closeText={closeText}
        />
      );
    case 'pending-vps-manager':
      if (decision.approvalCompleted) {
        return (
          <div className="approve-buttons">
            {renderCloseButton(closeModal, closeText)}
          </div>
        );
      }
      if (decision.canApprove) {
        return (
          <div className="approve-buttons">
            {renderCloseButton(closeModal, closeText)}
            <UndoButton handleClick={() => rejectAcc()} customText={rejectText} />
            <SaveButton handleClick={approveAcc} customText={approveText} />
          </div>
        );
      }
      return (
        <div className="approve-buttons">
          {renderCloseButton(closeModal, closeText)}
        </div>
      );
    case 'pending-vpc-manager':
      if (decision.approvalCompleted) {
        return (
          <div className="approve-buttons">
            {renderCloseButton(closeModal, closeText)}
          </div>
        );
      }
      if (decision.canApprove) {
        return (
          <div className="approve-buttons">
            {renderCloseButton(closeModal, closeText)}
            <UndoButton handleClick={() => rejectAcc()} customText={rejectText} />
            <SaveButton handleClick={approveAcc} customText={approveText} />
          </div>
        );
      }
      return (
        <div className="approve-buttons">
          {renderCloseButton(closeModal, closeText)}
        </div>
      );
    case 'pending-close-only':
      return (
        <div className="approve-buttons">
          <SaveButton handleClick={() => closeModal()} customText={closeText} />
        </div>
      );
    case 'pending-default':
      return (
        <div className="approve-buttons">
          <SaveButton handleClick={approveAcc} customText={approveText} />
          <UndoButton handleClick={() => rejectAcc()} customText={rejectText} />
        </div>
      );
    case 'reject':
      return (
        <div className="approve-buttons">
          <UndoButton handleClick={() => closeModal()} customText={closeText} customStyle={{ border: 'none' }} />
          {decision.canDelete && <UndoButton handleClick={deleteAcc} customText={deleteText} />}
          {decision.canEdit && (
            <SaveButton
              handleClick={() => setEditForm(true)}
              customText={editText}
            />
          )}
        </div>
      );
    case 'draft':
      return (
        <div className="approve-buttons">
          <UndoButton handleClick={() => closeModal()} customText={closeText} customStyle={{ border: 'none' }} />
          {decision.isDraftValid ? (
            decision.canEdit && (
              <UndoButton handleClick={() => setEditForm(true)} customText={editText} />
            )
          ) : (
            decision.canDelete && <UndoButton handleClick={deleteAcc} customText={deleteText} />
          )}
          {decision.isDraftValid ? (
            decision.canSubmitDraft && (
              <SaveButton
                handleClick={() => submitDraftToApproval()}
                customText={decision.submitDraftText === 'approve_accordance' ? approveText : t('send_for_approval')}
              />
            )
          ) : (
            decision.canEdit && (
              <SaveButton
                handleClick={() => setEditForm(true)}
                customText={editText}
              />
            )
          )}
        </div>
      );
    case 'valid':
      return (
        <div className="approve-buttons">
          <UndoButton
            handleClick={() => changeAccordanceStatus(AccordanceState.CANCELLED)}
            customText={t('cancel')}
            disabled={!decision.actionsEnabled}
          />
          <SaveButton
            handleClick={() => changeAccordanceStatus(AccordanceState.FULLFILLED)}
            customText={t('accomplish_accordance')}
            disabled={!decision.actionsEnabled}
          />
        </div>
      );
    case 'default-close':
    default:
      return <SaveButton handleClick={closeModal} customText={closeText} />;
  }
};

export default FooterButtons;
